import React, { FC, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';
import { Button } from 'antd';
import { CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';

import { IRootReducers } from 'src/reducers';
import { isAccountPage } from 'config/routes';
import { customFormatMessage } from 'utils';
import apiPaths from 'services/apiPaths';
import { putDataCall, postDataCall } from 'services/api';
import { IEditAccountData } from 'src/shared/models';
import {
  setProfilingBannerStatus,
  updateMCSubscription,
} from 'src/actions/authActions';
import { UserBannerProfiling, MCUserStatus } from 'src/shared/enums';
import { openToast } from 'src/components/general/Toast';

const { PROFILE_ONLY, NOT_IN_PROFILE, HIDE_BANNER } = UserBannerProfiling;

type Props = ReturnType<typeof mapDispatchToProps> &
  ReturnType<typeof mapStateToProps>;

const ProfilingBanner: FC<Props> = ({
  user,
  setProfilingBannerStatus,
  updateMCSubscription,
}): JSX.Element => {
  const { formatMessage } = useIntl();
  const { pathname } = useRouter();
  const getCurrentUrl = () => { return window.location.href }

  const [loading, setLoading] = useState(false);

  const handleCloseBanner = async () => {
    try {
      let userTemporalData: IEditAccountData = user.temporalData;

      if (
        user.temporalData?.profilingBannerStatus === PROFILE_ONLY ||
        user.temporalData?.profilingBannerStatus === NOT_IN_PROFILE
      ) {
        userTemporalData.profilingBannerStatus = HIDE_BANNER;
      } else if (isAccountPage(pathname)) {
        userTemporalData.profilingBannerStatus = HIDE_BANNER;
      } else {
        userTemporalData.profilingBannerStatus = PROFILE_ONLY;
      }

      await putDataCall({
        dataPath: apiPaths.PROFILE.UPDATE_USER_BANNER_STATUS,
        data: userTemporalData,
        callConfig: {},
      });

      setProfilingBannerStatus(userTemporalData.profilingBannerStatus);
    } catch (error) {
      console.error('Error closing banner: ', error);
    }
  };

  const handleAcceptProfiling = async () => {
    try {
      setLoading(true);

      await postDataCall({
        dataPath: apiPaths.MC_NEWSLETTER.SUBSCRIBE,
        data: {
            isDermo: user.dermo.isDermo,
            currentUrl: getCurrentUrl()
        },
        callConfig: {},
      });

      updateMCSubscription(MCUserStatus.SUBSCRIBED, MCUserStatus.SUBSCRIBED);

      openToast(
        formatMessage({ id: `page.account.newsletter.subscribe` }),
        'success',
        5
      );

      setLoading(false);
    } catch (error) {
      if (error.response) {
        const { data } = error.response;
        openToast(
          formatMessage(
            { id: `page.account-newsletter.response-error` },
            { code: data.code, error: data.error }
          ),
          'error',
          5
        );
      }
    }
  };

  return (
    <div className="bannerContainer">
      <div className="bannerContent">
        <div className="bannerContent__left">
          <div className="content__icon">
            <InfoCircleOutlined />
          </div>
          <div className="content__body">
            <div className="content__text">
              {customFormatMessage('banner-newsletter.info')}
              <span className="link-underscore">
                {customFormatMessage('app.privacy-policy-banner')}
              </span>
              .
            </div>
            <Button
              className="okButton"
              onClick={handleAcceptProfiling}
              loading={loading}
            >
              {customFormatMessage('app.accept')}
            </Button>
          </div>
        </div>
        <div className="bannerContent__right">
          <Button
            className="exitButton"
            onClick={handleCloseBanner}
            icon={<CloseOutlined />}
          />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: IRootReducers) => {
  return {
    user: state.auth.user,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators(
    { setProfilingBannerStatus, updateMCSubscription },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(ProfilingBanner);
